import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiBriefcase, FiRefreshCw, FiPlus, FiEdit2, FiCheck, FiSlash, FiUsers } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { legalReviewFirmsApi } from '../../api/smartTokenizerApi';
import { formatSectorLabel, mapLegalFirmFromApi, categoryIdsToSectorEnums } from '../../api/smartTokenizerMappers';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { getStoredSession } from '../../api/authStorage';
import { TokenizationSectorsCheckboxGrid } from '../shared/TokenizationSectorsCheckboxGrid';

const Page = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem;
  min-height: calc(100vh - 80px);
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: #4facfe;
  }
`;

const Lead = styled.p`
  color: #a0a0a0;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 1.5rem;
`;

const Submit = styled.button`
  padding: 0.65rem 1.25rem;
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  color: #0f0f23;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  padding: 0.4rem 0.65rem;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  &:hover:not(:disabled) {
    background: rgba(79, 172, 254, 0.2);
    border-color: #4facfe;
    color: #fff;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th,
  td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  th {
    color: #a0a0a0;
    font-weight: 600;
    background: rgba(0, 0, 0, 0.2);
  }

  td {
    color: #e8e8e8;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    color: #e0e0e0;
    font-size: 0.85rem;
    margin-bottom: 0.35rem;
  }

  input {
    width: 100%;
    max-width: 400px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 0.65rem 0.85rem;
    color: #fff;
    font-size: 0.95rem;

    &:focus {
      outline: none;
      border-color: #4facfe;
    }
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
`;

const Denied = styled.div`
  background: rgba(255, 100, 100, 0.08);
  border: 1px solid rgba(255, 100, 100, 0.25);
  border-radius: 12px;
  padding: 1.5rem;
  color: #fecaca;
  line-height: 1.6;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: #1a1a3e;
  border: 1px solid rgba(79, 172, 254, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  width: 100%;
  max-width: 480px;
`;

const SectorHint = styled.p`
  color: #a0a0a0;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0 0 0.75rem 0;
`;

const ModalTitle = styled.h3`
  margin-bottom: 1rem;
  color: #fff;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(p) => p.$tone || 'rgba(251, 191, 36, 0.2)'};
  color: ${(p) => p.$fg || '#fde68a'};
`;

const ActionsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
`;

function normalizeStatus(raw) {
  if (raw === 2 || raw === 'Approved' || raw === 'approved') return 'approved';
  if (raw === 3 || raw === 'Suspended' || raw === 'suspended') return 'suspended';
  return 'pending';
}

function statusLabel(s) {
  switch (s) {
    case 'approved':
      return 'Approved';
    case 'suspended':
      return 'Suspended';
    default:
      return 'Pending';
  }
}

function AdminLegalReviewFirms() {
  const { isAdmin } = useTokenIssuer();
  const hasSession = Boolean(getStoredSession()?.accessToken);

  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [regNumber, setRegNumber] = useState('');
  const [createSectorIds, setCreateSectorIds] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editFirm, setEditFirm] = useState(null);
  const [editOrg, setEditOrg] = useState('');
  const [editReg, setEditReg] = useState('');
  const [editSectorIds, setEditSectorIds] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const [usersOpen, setUsersOpen] = useState(false);
  const [usersFirm, setUsersFirm] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadFirms = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await legalReviewFirmsApi.list();
      setFirms(Array.isArray(data) ? data.map(mapLegalFirmFromApi) : []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to load firms');
      setFirms([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasSession && isAdmin) {
      loadFirms();
    }
  }, [hasSession, isAdmin, loadFirms]);

  const openCreate = () => {
    setOrgName('');
    setRegNumber('');
    setCreateSectorIds([]);
    setCreateOpen(true);
  };

  const toggleCreateSector = (id) => {
    setCreateSectorIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleEditSector = (id) => {
    setEditSectorIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (createSectorIds.length === 0) {
      toast.error('Select at least one tokenization sector.');
      return;
    }
    setCreating(true);
    try {
      await legalReviewFirmsApi.create({
        organizationName: orgName.trim(),
        registrationNumber: regNumber.trim(),
        sectors: categoryIdsToSectorEnums(createSectorIds)
      });
      toast.success('Legal reviewer firm created (pending approval).');
      setCreateOpen(false);
      await loadFirms();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (f) => {
    setEditFirm(f);
    setEditOrg(f.organizationName ?? f.OrganizationName ?? '');
    setEditReg(f.registrationNumber ?? f.RegistrationNumber ?? '');
    setEditSectorIds(f.sectorCategoryIds?.length ? [...f.sectorCategoryIds] : []);
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editFirm) return;
    if (editSectorIds.length === 0) {
      toast.error('Select at least one tokenization sector.');
      return;
    }
    const id = editFirm.id ?? editFirm.Id;
    setSavingEdit(true);
    try {
      await legalReviewFirmsApi.update(id, {
        organizationName: editOrg.trim(),
        registrationNumber: editReg.trim(),
        sectors: categoryIdsToSectorEnums(editSectorIds)
      });
      toast.success('Firm updated');
      setEditOpen(false);
      setEditFirm(null);
      await loadFirms();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleApprove = async (id, { reactivate = false } = {}) => {
    try {
      await legalReviewFirmsApi.approve(id);
      toast.success(reactivate ? 'Firm reactivated' : 'Firm approved');
      await loadFirms();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Approve failed');
    }
  };

  const handleSuspend = async (id) => {
    try {
      await legalReviewFirmsApi.suspend(id);
      toast.success('Firm suspended');
      await loadFirms();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Suspend failed');
    }
  };

  const openAssignedUsers = async (f) => {
    setUsersFirm(f);
    setUsersOpen(true);
    setLoadingUsers(true);
    setAssignedUsers([]);
    try {
      const id = f.id ?? f.Id;
      const { data } = await legalReviewFirmsApi.assignedUsers(id);
      setAssignedUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not load assigned users');
    } finally {
      setLoadingUsers(false);
    }
  };

  if (!hasSession) {
    return (
      <Page>
        <Title>
          <FiBriefcase />
          Legal reviewer firms
        </Title>
        <Denied>Sign in to access this page.</Denied>
      </Page>
    );
  }

  if (!isAdmin) {
    return (
      <Page>
        <Title>
          <FiBriefcase />
          Legal reviewer firms
        </Title>
        <Denied>Only administrators can manage legal reviewer firms.</Denied>
      </Page>
    );
  }

  return (
    <Page>
      <Title>
        <FiBriefcase />
        Legal reviewer firms
      </Title>
      <Lead>
        Register law desks that may perform legal reviews. Each firm must be <strong>approved</strong> before you assign{' '}
        <strong>Legal reviewer</strong> users to it in User management. Use <strong>Reactivate</strong> on a suspended
        firm to approve it again. Suspended firms cannot be used for new assignments.
      </Lead>

      <Toolbar>
        <Submit type="button" onClick={() => loadFirms()} disabled={loading}>
          <FiRefreshCw />
          {loading ? 'Refreshing…' : 'Refresh'}
        </Submit>
        <Submit type="button" onClick={openCreate} disabled={loading}>
          <FiPlus />
          Register firm
        </Submit>
      </Toolbar>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Registration #</th>
              <th>Sectors</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {firms.length === 0 && !loading && (
              <tr>
                <td colSpan={5} style={{ color: '#888' }}>
                  No firms yet.
                </td>
              </tr>
            )}
            {firms.map((f) => {
              const id = f.id ?? f.Id;
              const st = normalizeStatus(f.status ?? f.Status);
              const canApprove = st === 'pending' || st === 'suspended';
              const canSuspend = st !== 'suspended';
              const sectorLine =
                (f.sectors || []).map((s) => formatSectorLabel(s)).filter(Boolean).join(', ') || '—';
              return (
                <tr key={String(id)}>
                  <td>{f.organizationName ?? f.OrganizationName}</td>
                  <td>{f.registrationNumber ?? f.RegistrationNumber}</td>
                  <td style={{ maxWidth: '220px', fontSize: '0.82rem', color: '#c4c4c4' }}>{sectorLine}</td>
                  <td>
                    <StatusBadge
                      $tone={
                        st === 'approved'
                          ? 'rgba(34, 197, 94, 0.2)'
                          : st === 'suspended'
                            ? 'rgba(248, 113, 113, 0.2)'
                            : 'rgba(251, 191, 36, 0.2)'
                      }
                      $fg={st === 'approved' ? '#86efac' : st === 'suspended' ? '#fecaca' : '#fde68a'}
                    >
                      {statusLabel(st)}
                    </StatusBadge>
                  </td>
                  <td>
                    <ActionsGroup>
                      <IconButton type="button" onClick={() => openEdit(f)} disabled={loading}>
                        <FiEdit2 /> Edit
                      </IconButton>
                      <IconButton type="button" onClick={() => openAssignedUsers(f)} disabled={loading}>
                        <FiUsers /> Users
                      </IconButton>
                      <IconButton
                        type="button"
                        onClick={() => handleApprove(id, { reactivate: st === 'suspended' })}
                        disabled={loading || !canApprove}
                        title={st === 'suspended' ? 'Set firm back to approved' : 'Approve pending firm'}
                      >
                        <FiCheck /> {st === 'suspended' ? 'Reactivate' : 'Approve'}
                      </IconButton>
                      <IconButton type="button" onClick={() => handleSuspend(id)} disabled={loading || !canSuspend}>
                        <FiSlash /> Suspend
                      </IconButton>
                    </ActionsGroup>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>

      {createOpen && (
        <ModalOverlay onClick={(e) => e.target === e.currentTarget && !creating && setCreateOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: 'min(960px, calc(100vw - 2rem))' }}>
            <ModalTitle>Register legal reviewer firm</ModalTitle>
            <form onSubmit={handleCreate}>
              <Field>
                <label>Organization name</label>
                <input value={orgName} onChange={(e) => setOrgName(e.target.value)} required />
              </Field>
              <Field>
                <label>Registration number</label>
                <input value={regNumber} onChange={(e) => setRegNumber(e.target.value)} required />
              </Field>
              <Field>
                <label>Tokenization sectors</label>
                <SectorHint>
                  Select at least one. Same layout as <strong>Authorized sectors</strong> when editing an authorized
                  tokenizer — each maps to a backend <code style={{ color: '#9bd6ff' }}>SectorType</code>.
                </SectorHint>
                <TokenizationSectorsCheckboxGrid
                  selectedIds={createSectorIds}
                  onToggle={toggleCreateSector}
                />
              </Field>
              <Row style={{ marginTop: '1rem' }}>
                <Submit type="submit" disabled={creating}>
                  {creating ? 'Saving…' : 'Create'}
                </Submit>
                <IconButton type="button" onClick={() => setCreateOpen(false)} disabled={creating}>
                  Cancel
                </IconButton>
              </Row>
            </form>
          </Modal>
        </ModalOverlay>
      )}

      {editOpen && editFirm && (
        <ModalOverlay onClick={(e) => e.target === e.currentTarget && !savingEdit && setEditOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: 'min(960px, calc(100vw - 2rem))' }}>
            <ModalTitle>Edit firm</ModalTitle>
            <form onSubmit={saveEdit}>
              <Field>
                <label>Organization name</label>
                <input value={editOrg} onChange={(e) => setEditOrg(e.target.value)} required />
              </Field>
              <Field>
                <label>Registration number</label>
                <input value={editReg} onChange={(e) => setEditReg(e.target.value)} required />
              </Field>
              <Field>
                <label>Tokenization sectors</label>
                <SectorHint>
                  Select at least one. Same card layout as <strong>Authorized sectors</strong> on the authorized
                  tokenizer edit flow.
                </SectorHint>
                <TokenizationSectorsCheckboxGrid selectedIds={editSectorIds} onToggle={toggleEditSector} />
              </Field>
              <Row style={{ marginTop: '1rem' }}>
                <Submit type="submit" disabled={savingEdit}>
                  {savingEdit ? 'Saving…' : 'Save'}
                </Submit>
                <IconButton type="button" onClick={() => setEditOpen(false)} disabled={savingEdit}>
                  Cancel
                </IconButton>
              </Row>
            </form>
          </Modal>
        </ModalOverlay>
      )}

      {usersOpen && usersFirm && (
        <ModalOverlay onClick={(e) => e.target === e.currentTarget && setUsersOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <ModalTitle>
              Assigned users — {usersFirm.organizationName ?? usersFirm.OrganizationName}
            </ModalTitle>
            {loadingUsers ? (
              <p style={{ color: '#a0a0a0' }}>Loading…</p>
            ) : assignedUsers.length === 0 ? (
              <p style={{ color: '#a0a0a0' }}>No legal reviewers assigned to this firm yet.</p>
            ) : (
              <TableWrap style={{ marginTop: '0.75rem' }}>
                <Table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedUsers.map((u) => (
                      <tr key={String(u.userId ?? u.UserId)}>
                        <td>{u.email ?? u.Email}</td>
                        <td>{u.fullName ?? u.FullName}</td>
                        <td>{String(u.status ?? u.Status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
            <Row style={{ marginTop: '1rem' }}>
              <IconButton type="button" onClick={() => setUsersOpen(false)}>
                Close
              </IconButton>
            </Row>
          </Modal>
        </ModalOverlay>
      )}
    </Page>
  );
}

export default AdminLegalReviewFirms;
